import { ref, watch } from 'vue'

export type ThemeMode = 'system' | 'light' | 'dark' | 'eye-comfort' | 'geek'

const STORAGE_KEY = 'unmoored-theme'
const ALL_THEMES = ['system', 'light', 'dark', 'eye-comfort', 'geek']
const THEME_CLASSES = ['dark', 'theme-eye-comfort', 'theme-geek']
const theme = ref<ThemeMode>('system')
const isDark = ref(false)

let initialized = false

function clearThemeClasses() {
  THEME_CLASSES.forEach(c => document.documentElement.classList.remove(c))
}

function resolveAndApply(animate = true) {
  // Add transition class for smooth theme switching
  if (animate) {
    document.documentElement.classList.add('theme-transition')
    setTimeout(() => document.documentElement.classList.remove('theme-transition'), 400)
  }

  clearThemeClasses()
  if (theme.value === 'dark') {
    document.documentElement.classList.add('dark')
    isDark.value = true
  } else if (theme.value === 'eye-comfort') {
    document.documentElement.classList.add('theme-eye-comfort')
    isDark.value = false
  } else if (theme.value === 'geek') {
    document.documentElement.classList.add('dark', 'theme-geek')
    isDark.value = true
  } else if (theme.value === 'light') {
    isDark.value = false
  } else {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.classList.toggle('dark', dark)
    isDark.value = dark
  }
}

export function useDarkMode() {
  if (typeof window === 'undefined') return { theme, isDark }

  if (!initialized) {
    initialized = true
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && ALL_THEMES.includes(stored)) {
      theme.value = stored as ThemeMode
    }
    resolveAndApply()

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (theme.value === 'system') resolveAndApply()
    })
  }

  watch(theme, (val) => {
    localStorage.setItem(STORAGE_KEY, val)
    resolveAndApply(true)
  })

  return { theme, isDark }
}

export function initTheme() {
  if (typeof window === 'undefined') return
  const stored = localStorage.getItem(STORAGE_KEY)
  const mode: ThemeMode = stored && ALL_THEMES.includes(stored) ? stored as ThemeMode : 'system'
  THEME_CLASSES.forEach(c => document.documentElement.classList.remove(c))
  if (mode === 'dark') {
    document.documentElement.classList.add('dark')
  } else if (mode === 'eye-comfort') {
    document.documentElement.classList.add('theme-eye-comfort')
  } else if (mode === 'geek') {
    document.documentElement.classList.add('dark', 'theme-geek')
  } else if (mode === 'system') {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (dark) document.documentElement.classList.add('dark')
  }
}
